import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DownloadIcon from '@mui/icons-material/Download';
import BackupIcon from '@mui/icons-material/Backup';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';

// Import the base API URL from the api services
import { API_BASE_URL } from '../../services/api';

const PUJOY_LAST_UPDATE_KEY = 'pujoyStockLastUpdate';

const UtilityPage = () => {
  const [lastPujoyUpdate, setLastPujoyUpdate] = useState(() => localStorage.getItem(PUJOY_LAST_UPDATE_KEY));
  const [loading, setLoading] = useState({
    variantData: false,
    marketplaceData: false,
    databaseBackup: false,
    databaseBackupV2: false,
    pujoyStockUpdate: false
  });
  const [error, setError] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const adminUrl = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_ADMIN_URL_ID}/`;
  console.log("Admin URL:", adminUrl);
  
  // Function to download variant data
  const downloadVariantData = async () => {
    setLoading(prev => ({ ...prev, variantData: true }));
    try {
      // Direct API call for file download
      const response = await fetch(`${API_BASE_URL}/product/variants/variant_data_download/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'variants_data.xlsx';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }

      // Convert response to blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error("Download failed:", err);
      setError(`Failed to download variant data: ${err.message}`);
      setShowSnackbar(true);
    } finally {
      setLoading(prev => ({ ...prev, variantData: false }));
    }
  };

  // Function to download marketplace data
  const downloadMarketplaceData = async () => {
    setLoading(prev => ({ ...prev, marketplaceData: true }));
    try {
      // Direct API call for file download
      const response = await fetch(`${API_BASE_URL}/marketplace/marketplace-data-download/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'marketplace_data.xlsx';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }

      // Convert response to blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error("Download failed:", err);
      setError(`Failed to download marketplace data: ${err.message}`);
      setShowSnackbar(true);
    } finally {
      setLoading(prev => ({ ...prev, marketplaceData: false }));
    }
  };

  // Function to download database backup
  const downloadDatabaseBackup = async () => {
    setLoading(prev => ({ ...prev, databaseBackup: true }));
    try {
      // Direct API call for database backup download
      const response = await fetch(`${API_BASE_URL}/accounts/db-backup/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }


      const now = new Date();
      const timestamp = now.toISOString().replace(/[:]/g, '-').split('.')[0]; // e.g., "2025-07-11T14-30-00"
      const filename = `database_backup_${timestamp}.json`;


      // Convert response to blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error("Database backup download failed:", err);
      setError(`Failed to download database backup: ${err.message}`);
      setShowSnackbar(true);
    } finally {
      setLoading(prev => ({ ...prev, databaseBackup: false }));
    }
  };

  // Function to download database backup (dbbackup V2)
  const downloadDatabaseBackupV2 = async () => {
    setLoading(prev => ({ ...prev, databaseBackupV2: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/db-backup-v2/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'database_backup_v2';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      } else {
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:]/g, '-').split('.')[0];
        filename = `database_backup_v2_${timestamp}`;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error("Database backup V2 download failed:", err);
      setError(`Failed to download database backup V2: ${err.message}`);
      setShowSnackbar(true);
    } finally {
      setLoading(prev => ({ ...prev, databaseBackupV2: false }));
    }
  };



  const handleCloseSuccessSnackbar = () => {
    setShowSuccessSnackbar(false);
  };

  // Function to update stock on Pujoy
  const updateStockOnPujoy = async () => {
    setLoading(prev => ({ ...prev, pujoyStockUpdate: true }));
    try {
      const response = await fetch(
        `${API_BASE_URL}/product/variants/bulk_stock_update_to_pujoy/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const pujoyStatus = data.pujoy_status ? `\nPujoy Status: ${data.pujoy_status}` : '';
        const pujoyResponse = data.pujoy_response
          ? `\nPujoy Response: ${typeof data.pujoy_response === 'string' ? data.pujoy_response : JSON.stringify(data.pujoy_response)}`
          : '';
        throw new Error(`${data.error || `Error: ${response.status}`}${pujoyStatus}${pujoyResponse}`);
      }

      // Show success message
      const successMsg = `✓ Stock updated successfully!\nUpdated: ${data.updated_count}, Not Found: ${data.not_found_count}, Total Sent: ${data.variants_sent}`;
      setSuccessMessage(successMsg);
      setShowSuccessSnackbar(true);

      // Persist and display the last successful sync time.
      const lastUpdate = new Date().toISOString();
      localStorage.setItem(PUJOY_LAST_UPDATE_KEY, lastUpdate);
      setLastPujoyUpdate(lastUpdate);

    } catch (err) {
      console.error("Stock update failed:", err);
      setError(`Failed to update stock on Pujoy: ${err.message}`);
      setShowSnackbar(true);
    } finally {
      setLoading(prev => ({ ...prev, pujoyStockUpdate: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* <Typography variant="h4" gutterBottom component="h1">
        Utility Tools
      </Typography> */}

      <Grid container spacing={3}>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                DeskConnect
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Product Variants Data
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={loading.variantData ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                    onClick={downloadVariantData}
                    disabled={loading.variantData}
                  >
                    {loading.variantData ? 'Downloading...' : 'Download Variant Data'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Marketplace Data
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={loading.marketplaceData ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadMarketplaceData}
                    disabled={loading.marketplaceData}
                  >
                    {loading.marketplaceData ? 'Downloading...' : 'Download Marketplace Data'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Pujoy Stock Update
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Sync all active product stock to Pujoy seller dashboard
                  </Typography>
                  {lastPujoyUpdate && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Last update: {new Date(lastPujoyUpdate).toLocaleString()}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={loading.pujoyStockUpdate ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                    onClick={updateStockOnPujoy}
                    disabled={loading.pujoyStockUpdate}
                  >
                    {loading.pujoyStockUpdate ? 'Updating...' : 'Update Stock on Pujoy'}
                  </Button>
                </Box>

              </Box>
            </CardContent>
          </Card>
        </Grid>


        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Database Management
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Database Backup
                  </Typography>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={
                      loading.databaseBackup ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <BackupIcon />
                      )
                    }
                    onClick={downloadDatabaseBackup}
                    disabled={loading.databaseBackup}
                  >
                    {loading.databaseBackup ? 'Downloading...' : 'Download Database Backup'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Database Backup V2
                  </Typography>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={
                      loading.databaseBackupV2 ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <BackupIcon />
                      )
                    }
                    onClick={downloadDatabaseBackupV2}
                    disabled={loading.databaseBackupV2}
                  >
                    {loading.databaseBackupV2 ? 'Downloading...' : 'Download Database Backup V2'}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                   <Typography variant="subtitle1" gutterBottom>
                    Admin Panel
                  </Typography>
                  <Button
                    variant="contained" 
                    color="secondary"
                    startIcon={<AdminPanelSettingsIcon />}
                    href={adminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Go to Admin Page
                  </Button>
                </Box>

              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%', whiteSpace: 'pre-line' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UtilityPage;
